import { useEffect, useEffectEvent, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseTableStateFromUrl, writeTableStateToUrl } from './tableUrlParams';
import type { TableStore } from './UseTableStore';

const tableStateMatches = (left: unknown, right: unknown) =>
  JSON.stringify(left) === JSON.stringify(right);

export const useTableUrlSync = (store: TableStore, name: string) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isUpdatingFromUrl = useRef(false);
  const isInitialized = useRef(false);
  const lastWrittenSearch = useRef<string | null>(null);
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const syncStoreFromUrl = useEffectEvent((params: URLSearchParams) => {
    const defaults = store.getInitialState();
    const urlState = parseTableStateFromUrl(params, name);
    const nextState = {
      filters: urlState.filters ?? defaults.filters,
      pagination: { ...defaults.pagination, ...urlState.pagination },
      sorting: urlState.sorting ?? defaults.sorting,
    };
    const currentState = store.getState();
    if (
      tableStateMatches(nextState.filters, currentState.filters) &&
      tableStateMatches(nextState.pagination, currentState.pagination) &&
      tableStateMatches(nextState.sorting, currentState.sorting)
    ) {
      return;
    }

    isUpdatingFromUrl.current = true;
    try {
      store.setState(nextState);
    } finally {
      isUpdatingFromUrl.current = false;
    }
  });

  useEffect(() => {
    const initialize = () => {
      syncStoreFromUrl(searchParamsRef.current);
      isInitialized.current = true;
    };

    if (store.persist.hasHydrated()) initialize();
    else return store.persist.onFinishHydration(initialize);
  }, [store]);

  useEffect(() => {
    if (!isInitialized.current) return;

    const currentSearch = searchParams.toString();
    if (lastWrittenSearch.current === currentSearch) {
      lastWrittenSearch.current = null;
      return;
    }
    syncStoreFromUrl(searchParams);
  }, [searchParams]);

  useEffect(() => {
    const unsubscribe = store.subscribe((state, prevState) => {
      if (!isInitialized.current || isUpdatingFromUrl.current) return;

      const sortingChanged = state.sorting !== prevState.sorting;
      const paginationChanged = state.pagination !== prevState.pagination;
      const filtersChanged = state.filters !== prevState.filters;

      if (!sortingChanged && !paginationChanged && !filtersChanged) return;

      const scrollPosition = { left: window.scrollX, top: window.scrollY };
      setSearchParams(
        (prev) => {
          const next = writeTableStateToUrl(
            prev,
            name,
            state.sorting,
            state.pagination,
            state.filters
          );
          if (next.toString() === prev.toString()) return prev;
          lastWrittenSearch.current = next.toString();
          return next;
        },
        { replace: true }
      );
      window.requestAnimationFrame(() => window.scrollTo(scrollPosition));
    });

    return unsubscribe;
  }, [store, name, setSearchParams]);
};
